import { useState } from 'react'
import { Download, Check, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  RadioGroup,
  RadioGroupItem,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Progress,
} from '@wove/web-ui'
import { SourceType, useRestApi } from '@wove/api'
import { exportMultipleData } from '../../util/sources'

interface BatchExportDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedFileIds: string[]
}

const SOURCE_OPTIONS: { label: string; value: SourceType }[] = [
  {
    label: 'GPM',
    value: 'gpm',
  },
  {
    label: 'Freightos',
    value: 'freightos',
  },
  {
    label: 'Freightos GPM',
    value: 'freightos-gpm',
  },
  {
    label: 'Explorate',
    value: 'explorate',
  },
  {
    label: 'Winmore',
    value: 'winmore',
  },
  {
    label: 'Winmore GPM',
    value: 'winmore-gpm',
  },
]

export function BatchExportDialog({ isOpen, onClose, selectedFileIds }: BatchExportDialogProps) {
  const fileCount = selectedFileIds.length
  const [exportDestination, setExportDestination] = useState('download')
  const [exportSystem, setExportSystem] = useState('gpm')
  const [exportProgress, setExportProgress] = useState(0)
  const [exportStatus, setExportStatus] = useState<'idle' | 'processing' | 'success' | 'error'>(
    'idle',
  )
  const [exportMessage, setExportMessage] = useState('')

  const restApi = useRestApi()
  const handleExport = async () => {
    setExportStatus('processing')
    setExportProgress(0)
    setExportMessage('')

    try {
      const { success, failed } = await exportMultipleData(
        restApi,
        selectedFileIds,
        exportDestination === 'system' ? (exportSystem as SourceType) : 'original',
        (progress) => setExportProgress(progress),
      )

      if (failed === 0) {
        setExportStatus('success')
        setExportMessage(`${success} ${success === 1 ? 'file' : 'files'} exported successfully.`)
      } else if (success === 0) {
        setExportStatus('error')
        setExportMessage('Failed to export files. Please try again.')
      } else {
        setExportStatus('error')
        setExportMessage(
          `Exported ${success} files, ${failed} failed. Please try again for failed files.`,
        )
      }
    } catch (error) {
      setExportStatus('error')
      setExportMessage('An error occurred during export. Please try again.')
    }
  }

  const resetAndClose = () => {
    setExportDestination('download')
    setExportSystem('gpm')
    setExportStatus('idle')
    setExportProgress(0)
    setExportMessage('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-[500px] max-w-[95vw] max-h-[90vh] overflow-y-auto  p-8 bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">Batch Export</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Export {fileCount} selected files
          </DialogDescription>
        </DialogHeader>

        {exportStatus === 'idle' ? (
          <div className="space-y-4 py-3 sm:space-y-6 sm:py-4">
            {/* <div className="space-y-3 sm:space-y-4">
              <h3 className="text-sm font-medium">Export Format</h3>
              <RadioGroup
                value={exportFormat}
                onValueChange={setExportFormat}
                className="space-y-1.5 sm:space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="original" id="format-original" />
                  <Label htmlFor="format-original" className="text-xs sm:text-sm">
                    Original Format
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="standardized" id="format-standardized" />
                  <Label htmlFor="format-standardized" className="text-xs sm:text-sm">
                    Standardized Template (XLSX)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="csv" id="format-csv" />
                  <Label htmlFor="format-csv" className="text-xs sm:text-sm">
                    CSV
                  </Label>
                </div>
              </RadioGroup>
            </div> */}

            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-sm font-medium">Export Destination</h3>
              <RadioGroup
                value={exportDestination}
                onValueChange={setExportDestination}
                className="space-y-1.5 sm:space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="download" id="destination-download" />
                  <Label htmlFor="destination-download" className="text-xs sm:text-sm">
                    {/* Download as ZIP archive */}
                    Download original files
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="system" id="destination-system" />
                  <Label htmlFor="destination-system" className="text-xs sm:text-sm">
                    Export to external system
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {exportDestination === 'system' && (
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="export-system" className="text-xs sm:text-sm">
                  Select System
                </Label>
                <Select value={exportSystem} onValueChange={setExportSystem}>
                  <SelectTrigger id="export-system" className="text-xs sm:text-sm">
                    <SelectValue placeholder="Select system" />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCE_OPTIONS.map((opt) => {
                      return (
                        <SelectItem value={opt.value} className="text-xs sm:text-sm">
                          {opt.label}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ) : exportStatus === 'processing' ? (
          <div className="py-4 sm:py-6 space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg font-medium text-center">Exporting Files</h3>
            <Progress value={exportProgress} className="h-1.5 sm:h-2" />
            <p className="text-center text-xs sm:text-sm text-muted-foreground">
              {exportProgress < 100
                ? `Processing ${Math.round(exportProgress)}%...`
                : 'Finalizing export...'}
            </p>
          </div>
        ) : (
          <div className="py-4 sm:py-6 flex flex-col items-center justify-center text-center">
            {exportStatus === 'success' ? (
              <div className="space-y-3 sm:space-y-4">
                <div className="rounded-full bg-green-100 p-2 sm:p-3 mx-auto">
                  <Check className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                </div>
                <h3 className="text-base sm:text-lg font-medium">Export Successful</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">{exportMessage}</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                <div className="rounded-full bg-red-100 p-2 sm:p-3 mx-auto">
                  <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                </div>
                <h3 className="text-base sm:text-lg font-medium">Export Failed</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">{exportMessage}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          {exportStatus === 'idle' && (
            <>
              <Button
                variant="outline"
                onClick={resetAndClose}
                className="w-full sm:w-auto text-xs sm:text-sm"
              >
                Cancel
              </Button>
              <Button onClick={handleExport} className="w-full sm:w-auto text-xs sm:text-sm">
                <Download className="mr-1 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Export {fileCount} Files
              </Button>
            </>
          )}

          {exportStatus === 'processing' && (
            <Button
              variant="outline"
              onClick={resetAndClose}
              disabled
              className="w-full sm:w-auto text-xs sm:text-sm"
            >
              Exporting...
            </Button>
          )}

          {(exportStatus === 'success' || exportStatus === 'error') && (
            <>
              <Button
                variant="outline"
                onClick={resetAndClose}
                className="w-full sm:w-auto text-xs sm:text-sm"
              >
                Close
              </Button>
              {exportStatus === 'error' && (
                <Button onClick={handleExport} className="w-full sm:w-auto text-xs sm:text-sm">
                  Try Again
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
